import type { SeoLang } from './config';

type Copy = Record<SeoLang, { title: string; description: string }>;

const home: Copy = {
  fr: {
    title: 'BienLivrer — Petit commerce, grande livraison',
    description:
      'Commandez auprès de vos commerçants locaux à Biel/Bienne : livraison en tournée du soir ou retrait en magasin. Petit commerce, grande livraison.',
  },
  de: {
    title: 'BienLivrer — Kleiner Laden, grosse Lieferung',
    description:
      'Bestellen Sie bei lokalen Händlern in Biel/Bienne: Abendtour-Lieferung oder Abholung im Geschäft. Kleiner Laden, grosse Lieferung.',
  },
  en: {
    title: 'BienLivrer — Small shop, big-league delivery',
    description:
      'Order from local shops in Biel/Bienne: evening route delivery or in-store pickup. Small shop, big-league delivery.',
  },
  it: {
    title: 'BienLivrer — Piccolo negozio, grande consegna',
    description:
      'Ordina dai negozi locali a Biel/Bienne: consegna in tournée serale o ritiro in negozio. Piccolo negozio, grande consegna.',
  },
};

const landing: Copy = {
  fr: {
    title: 'BienLivrer — Livraison locale pour commerces suisses',
    description:
      'Plateforme de livraison locale à Biel/Bienne : commerçants de quartier, tournée du soir, retrait en magasin. Découvrez BienLivrer.',
  },
  de: {
    title: 'BienLivrer — Lokale Lieferung für Schweizer Geschäfte',
    description:
      'Lokale Lieferplattform in Biel/Bienne: Quartiergeschäfte, Abendtour, Abholung. Entdecken Sie BienLivrer.',
  },
  en: {
    title: 'BienLivrer — Local delivery for Swiss shops',
    description:
      'Local delivery platform in Biel/Bienne: neighbourhood shops, evening routes, pickup. Discover BienLivrer.',
  },
  it: {
    title: 'BienLivrer — Consegna locale per negozi svizzeri',
    description:
      'Piattaforma di consegna locale a Biel/Bienne: negozi di quartiere, tournée serale, ritiro. Scopri BienLivrer.',
  },
};

const support: Copy = {
  fr: {
    title: 'Aide & support — BienLivrer',
    description: 'FAQ, contacts et assistance BienLivrer pour clients, commerçants et livreurs.',
  },
  de: {
    title: 'Hilfe & Support — BienLivrer',
    description: 'FAQ, Kontakte und Unterstützung für Kunden, Händler und Lieferanten.',
  },
  en: {
    title: 'Help & support — BienLivrer',
    description: 'FAQ, contacts and support for customers, merchants and drivers on BienLivrer.',
  },
  it: {
    title: 'Aiuto e supporto — BienLivrer',
    description: 'FAQ, contatti e assistenza per clienti, commercianti e corrieri su BienLivrer.',
  },
};

const legal: Record<string, Copy> = {
  privacy: {
    fr: {
      title: 'Confidentialité (nLPD / RGPD) — BienLivrer',
      description: 'Politique de confidentialité BienLivrer : données collectées, destinataires, droits nLPD / RGPD.',
    },
    de: {
      title: 'Datenschutz (nDSG / DSGVO) — BienLivrer',
      description: 'Datenschutzerklärung von BienLivrer: erhobene Daten, Empfänger, Rechte nach nDSG / DSGVO.',
    },
    en: {
      title: 'Privacy (FADP / GDPR) — BienLivrer',
      description: 'BienLivrer privacy policy: data collected, recipients, and your FADP / GDPR rights.',
    },
    it: {
      title: 'Privacy (nLPD / GDPR) — BienLivrer',
      description: 'Informativa privacy BienLivrer: dati raccolti, destinatari e diritti nLPD / GDPR.',
    },
  },
  terms: {
    fr: {
      title: 'Conditions d’utilisation — BienLivrer',
      description: 'Conditions générales d’utilisation de la plateforme BienLivrer.',
    },
    de: {
      title: 'Nutzungsbedingungen — BienLivrer',
      description: 'Allgemeine Nutzungsbedingungen der Plattform BienLivrer.',
    },
    en: {
      title: 'Terms of use — BienLivrer',
      description: 'Terms of use for the BienLivrer platform.',
    },
    it: {
      title: 'Condizioni d’uso — BienLivrer',
      description: 'Condizioni generali di utilizzo della piattaforma BienLivrer.',
    },
  },
  impressum: {
    fr: {
      title: 'Mentions légales — BienLivrer',
      description: 'Mentions légales et informations sur l’éditeur de BienLivrer (Suisse).',
    },
    de: {
      title: 'Impressum — BienLivrer',
      description: 'Impressum und Herausgeberangaben von BienLivrer (Schweiz).',
    },
    en: {
      title: 'Legal notice — BienLivrer',
      description: 'Legal notice and publisher information for BienLivrer (Switzerland).',
    },
    it: {
      title: 'Note legali — BienLivrer',
      description: 'Note legali e informazioni sull’editore di BienLivrer (Svizzera).',
    },
  },
  cgv: {
    fr: {
      title: 'CGV — BienLivrer',
      description: 'Conditions générales de vente pour les commandes clients sur BienLivrer.',
    },
    de: {
      title: 'AGB — BienLivrer',
      description: 'Allgemeine Geschäftsbedingungen für Kundenbestellungen auf BienLivrer.',
    },
    en: {
      title: 'Terms of sale — BienLivrer',
      description: 'General terms of sale for customer orders on BienLivrer.',
    },
    it: {
      title: 'CGV — BienLivrer',
      description: 'Condizioni generali di vendita per gli ordini clienti su BienLivrer.',
    },
  },
  'merchant-terms': {
    fr: {
      title: 'Conditions commerçants — BienLivrer',
      description: 'Conditions applicables aux commerçants partenaires BienLivrer.',
    },
    de: {
      title: 'Händlerbedingungen — BienLivrer',
      description: 'Bedingungen für Partnerhändler von BienLivrer.',
    },
    en: {
      title: 'Merchant terms — BienLivrer',
      description: 'Terms applicable to BienLivrer partner merchants.',
    },
    it: {
      title: 'Condizioni commercianti — BienLivrer',
      description: 'Condizioni applicabili ai commercianti partner BienLivrer.',
    },
  },
};

const auth: Copy = {
  fr: {
    title: 'Connexion — BienLivrer',
    description: 'Connectez-vous à votre compte BienLivrer.',
  },
  de: {
    title: 'Anmelden — BienLivrer',
    description: 'Melden Sie sich bei Ihrem BienLivrer-Konto an.',
  },
  en: {
    title: 'Sign in — BienLivrer',
    description: 'Sign in to your BienLivrer account.',
  },
  it: {
    title: 'Accedi — BienLivrer',
    description: 'Accedi al tuo account BienLivrer.',
  },
};

const shop: Copy = {
  fr: {
    title: 'Commerce — BienLivrer',
    description: home.fr.description,
  },
  de: {
    title: 'Geschäft — BienLivrer',
    description: home.de.description,
  },
  en: {
    title: 'Shop — BienLivrer',
    description: home.en.description,
  },
  it: {
    title: 'Negozio — BienLivrer',
    description: home.it.description,
  },
};

const register: Copy = {
  fr: {
    title: 'Inscription — BienLivrer',
    description: 'Créez un compte client ou commerçant sur BienLivrer.',
  },
  de: {
    title: 'Registrierung — BienLivrer',
    description: 'Erstellen Sie ein Kunden- oder Händlerkonto bei BienLivrer.',
  },
  en: {
    title: 'Register — BienLivrer',
    description: 'Create a customer or merchant account on BienLivrer.',
  },
  it: {
    title: 'Registrazione — BienLivrer',
    description: 'Crea un account cliente o commerciante su BienLivrer.',
  },
};

const forgot: Copy = {
  fr: {
    title: 'Mot de passe oublié — BienLivrer',
    description: 'Réinitialisez le mot de passe de votre compte BienLivrer.',
  },
  de: {
    title: 'Passwort vergessen — BienLivrer',
    description: 'Setzen Sie das Passwort Ihres BienLivrer-Kontos zurück.',
  },
  en: {
    title: 'Forgot password — BienLivrer',
    description: 'Reset the password for your BienLivrer account.',
  },
  it: {
    title: 'Password dimenticata — BienLivrer',
    description: 'Reimpostate la password del vostro account BienLivrer.',
  },
};

const reset: Copy = {
  fr: {
    title: 'Nouveau mot de passe — BienLivrer',
    description: 'Choisissez un nouveau mot de passe pour votre compte BienLivrer.',
  },
  de: {
    title: 'Neues Passwort — BienLivrer',
    description: 'Wählen Sie ein neues Passwort für Ihr BienLivrer-Konto.',
  },
  en: {
    title: 'New password — BienLivrer',
    description: 'Choose a new password for your BienLivrer account.',
  },
  it: {
    title: 'Nuova password — BienLivrer',
    description: 'Scegliete una nuova password per il vostro account BienLivrer.',
  },
};

const notFound: Copy = {
  fr: {
    title: 'Page introuvable — BienLivrer',
    description: 'Cette page n’existe pas sur BienLivrer. Retour à l’accueil pour commander chez un commerce local.',
  },
  de: {
    title: 'Seite nicht gefunden — BienLivrer',
    description: 'Diese Seite gibt es bei BienLivrer nicht. Zurück zur Startseite für lokale Bestellungen.',
  },
  en: {
    title: 'Page not found — BienLivrer',
    description: 'This page does not exist on BienLivrer. Go back home to order from a local shop.',
  },
  it: {
    title: 'Pagina non trovata — BienLivrer',
    description: 'Questa pagina non esiste su BienLivrer. Tornate alla home per ordinare da un negozio locale.',
  },
};

export function pickCopy(copy: Copy, lang: SeoLang) {
  return copy[lang] ?? copy.fr;
}

export const staticSeoCopy = {
  home,
  landing,
  support,
  legal,
  auth,
  register,
  forgot,
  reset,
  shop,
  notFound,
};
